import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";

import { ViewMode } from "@/components/atoms/core/ViewToggle";

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (viewMode: ViewMode) => void;
  handleViewModeChange: (
    _event: React.MouseEvent<HTMLElement>,
    newViewMode: ViewMode | null
  ) => void;
}

interface ViewModeProviderProps {
  children: ReactNode;
  defaultValue?: ViewMode;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(
  undefined
);

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({
  children,
  defaultValue = "cards",
}) => {
  const [viewMode, setViewModeState] = useState<ViewMode>(defaultValue);

  const setViewMode = useCallback((newViewMode: ViewMode) => {
    setViewModeState(newViewMode);
  }, []);

  const handleViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode | null) => {
      if (newViewMode !== null) {
        setViewMode(newViewMode);
      }
    },
    [setViewMode]
  );

  const value = useMemo<ViewModeContextType>(
    () => ({ viewMode, setViewMode, handleViewModeChange }),
    [viewMode, setViewMode, handleViewModeChange]
  );

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewModeContext = (): ViewModeContextType => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error(
      "useViewModeContext must be used within a ViewModeProvider"
    );
  }
  return context;
};
